import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, ArrowRight, Calendar, MapPin } from "lucide-react";
import api from "../lib/api";
import Logo from "./Logo";

export default function PromoPopup() {
  const [popup, setPopup] = useState(null);
  const [event, setEvent] = useState(null);
  const [closed, setClosed] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const dismissed = sessionStorage.getItem("promo-dismissed");
    if (dismissed) { setClosed(true); return; }
    api.get("/popup").then(async (r) => {
      setPopup(r.data);
      if (r.data?.event_slug) {
        try {
          const ev = await api.get(`/events/${r.data.event_slug}`);
          setEvent(ev.data);
        } catch { /* event may have been deleted */ }
      }
    }).catch(() => {});
  }, []);

  const onInscriptionFlow = loc.pathname.startsWith("/i/") || loc.pathname.startsWith("/inscription-confirmee/");
  if (onInscriptionFlow) return null;
  if (!popup?.enabled || closed) return null;

  const close = () => { setClosed(true); sessionStorage.setItem("promo-dismissed", "1"); };
  const slug = popup.event_slug;

  return (
    <div className="fixed inset-0 z-[60] grid place-items-center p-4 animate-fade-up" data-testid="promo-popup">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={close} />
      <div className="relative bg-white max-w-2xl w-full shadow-2xl rounded-sm overflow-hidden">
        <button onClick={close} aria-label="Fermer" data-testid="promo-close"
                className="absolute top-3 right-3 w-9 h-9 grid place-items-center text-white bg-ink/40 hover:bg-ink/70 rounded-sm transition-colors z-10">
          <X className="w-4 h-4" />
        </button>

        {/* Premium designed banner — institutional */}
        <div className="relative bg-ink text-white px-8 py-10 overflow-hidden grain">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_30%,rgba(212,175,55,0.18),transparent_60%)]" />
          <div className="absolute -bottom-12 -right-12 w-64 h-64 rounded-full bg-gold/10 blur-3xl" />
          <div className="relative flex items-start gap-5">
            <Logo className="w-16 h-16 shrink-0" />
            <div className="min-w-0">
              <div className="eyebrow eyebrow-on-dark text-[0.65rem]">Événement officiel</div>
              <h3 className="font-serif text-2xl lg:text-3xl mt-2 leading-tight">
                {popup.title || "Inscrivez-vous"}
              </h3>
              {(popup.subtitle || event?.theme) && (
                <p className="mt-3 text-white/80 text-sm leading-relaxed italic font-serif">
                  « {popup.subtitle || event?.theme} »
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Stages preview if multi-stage */}
        {event?.stages?.length > 0 && (
          <div className="px-8 pt-6 pb-2 grid grid-cols-1 sm:grid-cols-3 gap-3">
            {event.stages.map((s, i) => (
              <div key={i} className="border-l-2 border-gold pl-3">
                <div className="text-[0.65rem] uppercase tracking-[0.18em] font-semibold text-gold-dark">Étape {i + 1}</div>
                <div className="text-sm font-semibold text-ink mt-0.5">{s.city}</div>
                <div className="text-xs text-foreground/60 flex items-center gap-1 mt-0.5">
                  <Calendar className="w-3 h-3" />
                  {new Date(s.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" })}
                  {s.time && ` · ${s.time}`}
                </div>
              </div>
            ))}
          </div>
        )}

        {!event?.stages?.length && event && (
          <div className="px-8 pt-6 flex items-center gap-5 text-sm text-foreground/70">
            <div className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-gold-dark" /> {new Date(event.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</div>
            <div className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-gold-dark" /> {event.hide_location ? "Lieu communiqué après inscription" : `${event.city}, ${event.country}`}</div>
          </div>
        )}

        <div className="px-8 py-6 flex flex-col sm:flex-row gap-3">
          {slug && (
            <Link to={`/evenements/${slug}`} className="btn-outline flex-1 justify-center" onClick={close} data-testid="promo-discover">
              Découvrir l'événement
            </Link>
          )}
          {slug && (
            <Link to={`/i/${slug}`} className="btn-primary flex-1 justify-center" onClick={close} data-testid="promo-cta">
              S'inscrire <ArrowRight className="w-4 h-4" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
